<template>
  <div>
    <FlashNotifications ref="flash" />

    <div class="px-4 sm:px-6 py-5 max-w-5xl mx-auto">

      <!-- Date range + Export row -->
      <div class="flex items-center justify-between gap-3 mb-3">
        <!-- Date range picker -->
        <div class="relative" ref="datePickerRef">
          <button @click.stop="datePickerOpen = !datePickerOpen"
            class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
            </svg>
            {{ displayRange }}
            <svg class="w-3 h-3 text-gray-400 transition-transform" :class="datePickerOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
            </svg>
          </button>

          <!-- Dropdown -->
          <div v-if="datePickerOpen" @click.stop
            class="absolute left-0 top-full mt-1.5 z-20 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 w-64">
            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Date range (booking date)</p>
            <div class="space-y-2.5">
              <div>
                <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</label>
                <input type="date" v-model="pendingFrom"
                  class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</label>
                <input type="date" v-model="pendingTo"
                  class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div class="flex items-center gap-2 mt-3.5">
              <button @click="applyDateRange"
                class="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors">
                Apply
              </button>
              <button @click="resetDateRange"
                class="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                Reset
              </button>
            </div>
          </div>
        </div>

        <BookingExportMenu :event-id="event.id" :open="exportMenuOpen" @toggle="exportMenuOpen = !exportMenuOpen" />
      </div>

      <!-- Category pills + counts -->
      <div class="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        <button v-for="cat in categories" :key="cat.key" @click="switchTab(cat.key)"
          class="px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 flex items-center gap-1.5"
          :class="activeCategory === cat.key
            ? 'bg-blue-600 text-white shadow-sm'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'">
          {{ cat.label }}
          <span v-if="counts[cat.key] !== null"
            class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none"
            :class="activeCategory === cat.key
              ? 'bg-white/25 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'">
            {{ counts[cat.key] > 999 ? '999+' : counts[cat.key] }}
          </span>
          <span v-else-if="countsLoading"
            class="inline-block w-3 h-3 rounded-full bg-current opacity-30 animate-pulse">
          </span>
        </button>
      </div>

      <BookingCategoryList
        :key="listKey"
        :event-id="event.id"
        :category="activeCategory"
        :flash="flash"
        :date-from="dateFrom"
        :date-to="dateTo"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useConstants } from 'composables/useConstants.js'
import { useEventBookingsStore } from 'stores/eventBookings.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import BookingCategoryList from 'controllers/vue_components/events/bookings/BookingCategoryList'
import BookingExportMenu from 'controllers/vue_components/events/bookings/BookingExportMenu'

// The event is loaded once by EventLayout, which also renders the sub nav.
defineProps({
  event: { type: Object, required: true },
})

const route   = useRoute()
const { I18N } = useConstants()
const flash   = ref(null)
const store   = useEventBookingsStore()

const eventId = route.params.id

const activeCategory = ref('recent')
const exportMenuOpen = ref(false)
const datePickerRef  = ref(null)
const datePickerOpen = ref(false)

// Date range — default: 7 days ago to 7 days from now
function toISODate(d) { return d.toISOString().slice(0, 10) }
const defaultFrom = toISODate(new Date())
const defaultTo   = toISODate(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000))

const dateFrom   = ref(defaultFrom)
const dateTo     = ref(defaultTo)
const pendingFrom = ref(defaultFrom)
const pendingTo   = ref(defaultTo)

// Forces BookingCategoryList to remount (and refetch) when date range changes
const listKey = computed(() => `${activeCategory.value}-${dateFrom.value}-${dateTo.value}`)

const displayRange = computed(() => {
  const fmt = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${fmt(dateFrom.value)} to ${fmt(dateTo.value)}`
})

const countsState   = computed(() => store.countsState(eventId))
const counts        = computed(() => countsState.value)
const countsLoading = computed(() => countsState.value.loading)

const categories = computed(() => [
  { key: 'recent',   label: I18N.application.index.recently_booked },
  { key: 'upcoming', label: I18N.application.index.upcoming },
  { key: 'past',     label: I18N.application.index.past },
  { key: 'canceled', label: I18N.application.index.canceled },
  { key: 'archived', label: I18N.application.index.archived },
])

function switchTab(key) {
  activeCategory.value = key
  store.fetchCounts(eventId, dateFrom.value, dateTo.value)
}

function applyDateRange() {
  if (!pendingFrom.value || !pendingTo.value) return
  dateFrom.value = pendingFrom.value
  dateTo.value   = pendingTo.value
  datePickerOpen.value = false
  store.fetchCounts(eventId, dateFrom.value, dateTo.value)
}

function resetDateRange() {
  pendingFrom.value = defaultFrom
  pendingTo.value   = defaultTo
  dateFrom.value    = defaultFrom
  dateTo.value      = defaultTo
  datePickerOpen.value = false
  store.fetchCounts(eventId, dateFrom.value, dateTo.value)
}

function closeMenusOnOutsideClick(e) {
  if (exportMenuOpen.value) exportMenuOpen.value = false
  if (datePickerOpen.value && datePickerRef.value && !datePickerRef.value.contains(e.target)) {
    datePickerOpen.value = false
  }
}

onMounted(() => {
  store.fetchCounts(eventId, dateFrom.value, dateTo.value)
  document.addEventListener('click', closeMenusOnOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', closeMenusOnOutsideClick)
})
</script>
